import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import ArrowIcon from './ArrowIcon';
import BlogCoverArt from './BlogCoverArt';
import { subscribePublishedPosts } from '../data/blogService';

function formatDate(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/* ===== Single article card (3D tilt) ===== */
function ArticleCard({ idx, post }) {
  const ref = useRef(null);

  const onMove = (e) => {
    if (!ref.current) return;
    const r = ref.current.getBoundingClientRect();
    const x = (e.clientX - r.left) / r.width - 0.5;
    const y = (e.clientY - r.top) / r.height - 0.5;
    ref.current.style.transform = `perspective(1000px) rotateY(${x * 8}deg) rotateX(${-y * 8}deg) translateZ(8px)`;
  };
  const onLeave = () => {
    if (!ref.current) return;
    ref.current.style.transform = 'perspective(1000px) rotateY(0) rotateX(0) translateZ(0)';
  };

  return (
    <div
      ref={ref}
      className="tilt-card hoverable"
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      style={{
        background: 'white',
        border: '1px solid var(--line)',
        borderRadius: 24,
        padding: 8,
        transition: 'transform 200ms ease, box-shadow 300ms ease',
        transitionDelay: `${idx * 80}ms`,
      }}
    >
      <div
        className="service-img-area"
        style={{
          position: 'relative',
          height: 220,
          borderRadius: 18,
          overflow: 'hidden',
        }}
      >
        <BlogCoverArt
          image={post.image}
          alt={post.title}
          rgb={post.rgb}
          badge={post.categoryLabel}
          meta={[post.readTime, formatDate(post.date)]}
          compact
        />
      </div>

      <div style={{ padding: '24px 18px 20px' }}>
        <h3
          style={{
            fontFamily: "'Anton', sans-serif",
            fontSize: 22,
            fontWeight: 400,
            marginBottom: 10,
            lineHeight: 1.15,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {post.title}
        </h3>
        <p
          style={{
            fontSize: 14.5, lineHeight: 1.6, color: 'var(--ink-soft)', marginBottom: 22,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {post.excerpt}
        </p>
        <Link
          to={`/blog/${post.slug}`}
          className="hoverable"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 10,
            padding: '12px 20px',
            background: 'linear-gradient(135deg, var(--blue), #1A50FF)',
            color: 'white',
            borderRadius: 999,
            fontSize: 13,
            fontWeight: 700,

            boxShadow: '0 6px 18px rgba(24,24,232,0.28)',
          }}
        >
          Read Article <ArrowIcon size={12} />
        </Link>
      </div>
    </div>
  );
}

/* ===== Services section ===== */
export default function Services() {
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    const unsubscribe = subscribePublishedPosts(setPosts);
    return unsubscribe;
  }, []);

  const featured = posts.filter((p) => p.featured);
  const articles = (featured.length >= 3 ? featured : posts).slice(0, 3);

  return (
    <section id="services" className="sect-py" style={{ padding: 'clamp(40px, 6vw, 72px) 0', background: 'transparent' }}>
      <div style={{ maxWidth: 1320, margin: '0 auto', padding: '0 clamp(16px, 4vw, 32px)' }}>
        <div className="reveal" style={{ textAlign: 'center', marginBottom: 56 }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 12, marginBottom: 18 }}>
            <span
              style={{
                fontSize: 11,
                color: 'var(--red)',
              }}
            >
              /
            </span>
            <span style={{ width: 30, height: 1, background: 'var(--ink)' }} />
          </div>
          <h2
            style={{
              fontFamily: "'Anton', sans-serif",
              fontSize: 'clamp(40px, 6vw, 72px)',
              fontWeight: 400,
              lineHeight: 1,
            }}
          >
            ARTICLES{' '}
            <span
              style={{
                fontFamily: "'Instrument Serif', serif",
                fontStyle: 'italic',
               
                fontWeight: 400,
                color: 'var(--red)',
              }}
            >
              & notes
            </span>
          </h2>
          <p style={{ marginTop: 16, fontSize: 16, color: 'var(--ink-soft)', maxWidth: 560, margin: '16px auto 0' }}>
            Deep dives, field guides, and strategic insights from the engineers behind the build.
          </p>
        </div>

        <div
          className="services-cards-grid"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: 24,
          }}
        >
          {articles.map((post, i) => (
            <ArticleCard key={post.slug} idx={i} post={post} />
          ))}
        </div>

        <div className="reveal" style={{ marginTop: 56, textAlign: 'center' }}>
          <Link
            to="/blog"
            className="hoverable"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 12,
              padding: '18px 32px',
              background: 'linear-gradient(135deg, var(--blue), #1A50FF)',
              color: 'white',
              borderRadius: 999,
              fontSize: 15,
              fontWeight: 700,
              
              boxShadow: '0 12px 32px rgba(24,24,232,0.32)',
            }}
          >
            View All Articles
            <span
              style={{
                display: 'inline-flex',
                width: 28,
                height: 28,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.18)',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <ArrowIcon size={14} />
            </span>
          </Link>
        </div>
      </div>
    </section>
  );
}
